/**
 * PLAT-11224 (Faster TTFE) — rendition-readiness helper usable outside the
 * components layer (stores import this). `Dashboard/utils.ts` owns the
 * dashboard-facing readiness predicates; the 270p check here mirrors its
 * `isStoryCompressedVideoReady` semantics for the AWS pipeline.
 */

import { isAudioThenVideoEnabled } from './audioThenVideoFlag';
import { isRenditionHandOffHeld } from './manualRenditionHandOff';

/** Structural subset of a story record needed to judge rendition readiness. */
export interface RenditionReadinessCarrier {
  id?: string;
  useAws?: boolean;
  uploadLog?: {
    handleFailure?: Record<string, unknown> | null;
    extractThumbnailsFromLowRes?: { status?: string } | null;
    compressVideo?: {
      output?: { jobs?: Array<{ resolution?: string; status?: string }> };
    } | null;
  } | null;
}

/**
 * True while an AWS-pipeline story's compressed renditions (270p et al.) are
 * still being produced — absence of a rendition URL then means "not yet",
 * not "never". Under audio-first editing the editor opens before renditions
 * exist, so a missing 270p must surface a placeholder and never silently
 * fall back to the multi-GB unprocessed original. Falling back stays
 * legitimate only when the absence is permanent: legacy (non-AWS) stories
 * and failed pipelines.
 */
export function isCompressedRenditionPending(
  story: RenditionReadinessCarrier | null | undefined,
): boolean {
  // Kill switch (PLAT-11222): with audio-then-video off, no surface treats a
  // missing rendition as "not yet" — every path takes its pre-TTFE branch.
  if (!isAudioThenVideoEnabled()) return false;
  if (!story?.useAws) return false;
  const log = story.uploadLog;
  const failed = !!log?.handleFailure;
  const pending =
    !log ||
    (!failed &&
      !(
        log.extractThumbnailsFromLowRes?.status === 'success' ||
        !!log.compressVideo?.output?.jobs?.some(
          j =>
            j.resolution === '270' &&
            (j.status || '').toLowerCase() === 'complete',
        )
      ));
  // Dev override: keep the pending state until `__ttfe.upgrade()`. Called on
  // every evaluation — it records the stories it sees pending.
  const held = isRenditionHandOffHeld(story.id, pending, failed);
  return pending || held;
}
